import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Outlet, useParams, Link, useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { LegalLinks } from "@/components/LegalLinks";
import { Button } from "@/components/ui/button";
import { ShoppingCart, User, LogOut, Package } from "lucide-react";
import FullScreenLoader from "@/components/FullScreenLoader";

const StoreContext = createContext(null);
export const useStore = () => useContext(StoreContext);

export default function StoreLayout() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, storefront, logout } = useAuth();
  const [store, setStore] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const cartKey = `drelik_cart_${slug}`;
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`drelik_cart_${slug}`)) || []; } catch { return []; }
  });

  const isCustomer = user && user.tenant_role === "customer" && storefront?.slug === slug;

  useEffect(() => {
    api.get(`/public/stores/${slug}`).then((r) => setStore(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => { localStorage.setItem(cartKey, JSON.stringify(cart)); }, [cart, cartKey]);

  const addToCart = useCallback((product, qty = 1) => {
    setCart((c) => {
      const existing = c.find((i) => i.product_id === product.id);
      if (existing) return c.map((i) => i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i);
      return [...c, { product_id: product.id, name: product.name, price: product.price, image: product.images?.[0], quantity: qty }];
    });
  }, []);

  const updateQty = useCallback((pid, qty) => {
    setCart((c) => qty <= 0 ? c.filter((i) => i.product_id !== pid) : c.map((i) => i.product_id === pid ? { ...i, quantity: qty } : i));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);

  const doLogout = async () => { await logout(); navigate(`/store/${slug}`); };

  if (notFound) return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
      <Package className="h-12 w-12 text-muted-foreground/40 mb-4" />
      <h1 className="text-2xl font-bold">Storefront not found</h1>
      <p className="text-muted-foreground mt-2">This store may not exist or has been suspended.</p>
      <Link to="/"><Button className="mt-6 rounded-full">Go to Drelik</Button></Link>
    </div>
  );
  if (!store) return <FullScreenLoader />;

  return (
    <StoreContext.Provider value={{ store, slug, cart, addToCart, updateQty, clearCart, cartCount, isCustomer }}>
      <div className="min-h-screen bg-background flex flex-col">
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border/60">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
            <Link to={`/store/${slug}`} className="flex items-center gap-2.5 min-w-0" data-testid="store-home-link">
              {store.logo_url ? <img src={fileUrl(store.logo_url)} alt="" className="h-9 w-9 rounded-lg object-cover" />
                : <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center text-primary-foreground font-bold">{store.name[0]}</div>}
              <span className="font-heading font-bold text-lg truncate">{store.name}</span>
            </Link>
            <div className="flex items-center gap-2">
              <Link to={`/store/${slug}/cart`} className="relative p-2 rounded-lg hover:bg-secondary transition-colors" data-testid="cart-link">
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 && <span className="absolute -top-0.5 -right-0.5 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-semibold">{cartCount}</span>}
              </Link>
              {isCustomer ? (
                <>
                  <Link to={`/store/${slug}/account`}><Button variant="ghost" size="sm" data-testid="account-link"><User className="h-4 w-4 mr-1.5" /> Account</Button></Link>
                  <button onClick={doLogout} className="p-2 text-muted-foreground hover:text-destructive" data-testid="store-logout-btn"><LogOut className="h-5 w-5" /></button>
                </>
              ) : (
                <Link to={`/store/${slug}/login`}><Button size="sm" className="rounded-full" data-testid="store-login-btn">Sign in</Button></Link>
              )}
            </div>
          </div>
        </header>
        <main className="flex-1"><Outlet /></main>
        <footer className="border-t border-border mt-12">
          <div className="max-w-6xl mx-auto px-6 py-8 text-sm text-muted-foreground flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row justify-between gap-3">
              <div>
                <p className="font-medium text-foreground">{store.name}</p>
                {store.contact_email && <p>{store.contact_email}</p>}
                {store.business_hours && <p>{store.business_hours}</p>}
              </div>
              <p>Powered by Drelik</p>
            </div>
            <LegalLinks />
          </div>
        </footer>
      </div>
    </StoreContext.Provider>
  );
}
